import { useCallback, useEffect, useState } from 'react';
import {
  Plug, Plus, Trash2, Wifi, RefreshCw, Loader2, KeyRound, ExternalLink,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api/client.js';

/**
 * /integrations — BUILD-QUEUE §7b.
 *
 * One place listing EVERY connected third-party account across features,
 * grouped by purpose. This page owns NO data of its own: it reads the
 * aggregate from GET /api/integrations, and every action (add / test /
 * delete) is sent to the feature's own existing endpoints (`group.base`).
 * Feature pages keep their own pickers reading the same rows.
 */
export function Integrations() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await api('/api/integrations');
      setData(d);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'network_error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-t3">
          Every connected third-party account across the panel, in one place.
          Adding, testing and removing accounts here uses the same encrypted
          stores the feature pages do — nothing is duplicated.
        </p>
      </div>

      {error && (
        <div className="bg-err-bg border border-err-bd text-err text-sm rounded-md px-4 py-3">
          {error}
        </div>
      )}

      {data === null ? (
        <p className="text-sm text-t3">Loading…</p>
      ) : (
        <div className="space-y-8">
          {data.groups.map((group) => (
            <IntegrationGroup key={group.key} group={group} onChanged={load} />
          ))}
          {data.total === 0 && (
            <div className="bg-white border border-bd rounded-lg p-8 text-center text-sm text-t3">
              <Plug className="w-8 h-8 mx-auto mb-3 text-ic-light" />
              No third-party accounts connected yet. Add one under any category
              above, or from its feature page.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    ok: 'bg-s-green text-ok border-bd',
    failed: 'bg-err-bg text-err border-err-bd',
    new: 'bg-s-3 text-t2 border-bd',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${styles[status] ?? styles.new}`}>
      {status}
    </span>
  );
}

function IntegrationGroup({ group, onChanged }) {
  const [showForm, setShowForm] = useState(false);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-t1 uppercase tracking-wide">
            {group.label}
          </h2>
          <span className="text-xs text-t3">
            {group.accounts.length} account{group.accounts.length === 1 ? '' : 's'}
          </span>
          <Link
            to={group.feature.path}
            className="inline-flex items-center gap-1 text-xs text-blue hover:text-blue-deep"
            title={`Open ${group.feature.name}`}
          >
            <ExternalLink className="w-3.5 h-3.5" /> {group.feature.name}
          </Link>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-blue text-white hover:bg-blue-deep"
        >
          <Plus className="w-3.5 h-3.5" /> Add account
        </button>
      </div>

      {showForm && (
        <AddAccountForm
          group={group}
          onCreated={() => { setShowForm(false); onChanged(); }}
        />
      )}

      {group.accounts.length === 0 ? (
        <p className="text-sm text-t3 bg-white border border-bd rounded-lg px-4 py-4">
          No {group.label.toLowerCase()} connected.
        </p>
      ) : (
        <div className="bg-white border border-bd rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-t3 border-b border-bd">
                <th className="px-4 py-3">Provider</th>
                <th className="px-4 py-3">Label</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Last checked</th>
                <th className="px-4 py-3">Added</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {group.accounts.map((a) => (
                <AccountRow key={a.id} group={group} account={a} onChanged={onChanged} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function AccountRow({ group, account, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  async function handleTest() {
    setBusy(true);
    setTestResult(null);
    try {
      const r = await api(`${group.base}/${account.id}/test`, { method: 'POST' });
      setTestResult({ ok: true, detail: r.email ?? r.detail ?? 'connection ok' });
    } catch (err) {
      setTestResult({ ok: false, detail: err.body?.detail ?? err.message });
    } finally {
      setBusy(false);
      onChanged();
    }
  }

  async function handleDelete() {
    const typed = window.prompt(
      `Type the account label to confirm removal:\n${account.label}\n\n(Removes the encrypted credentials from the panel — nothing happens on the provider side.)`,
    );
    if (typed === null) return;
    if (typed !== account.label) {
      window.alert('Label did not match — not removed.');
      return;
    }
    setBusy(true);
    try {
      await api(`${group.base}/${account.id}`, {
        method: 'DELETE',
        body: { confirm_label: typed },
      });
      onChanged();
    } catch (err) {
      window.alert(`Delete failed: ${err.message}`);
      setBusy(false);
    }
  }

  return (
    <tr className="border-b border-bd last:border-0">
      <td className="px-4 py-3">
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border bg-s-2 text-t2 border-bd">
          {account.provider_label}
        </span>
      </td>
      <td className="px-4 py-3 font-medium text-t1">{account.label}</td>
      <td className="px-4 py-3">
        <StatusBadge status={account.status} />
        {testResult && (
          <span className={`ml-2 text-xs ${testResult.ok ? 'text-ok' : 'text-err'}`}>
            {testResult.detail}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-t3 text-xs">
        {account.last_checked_at ? new Date(`${account.last_checked_at}Z`).toLocaleString() : '—'}
      </td>
      <td className="px-4 py-3 text-t3 text-xs">
        {new Date(`${account.created_at}Z`).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            disabled={busy}
            onClick={handleTest}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd text-t2 hover:bg-s-2 disabled:opacity-50"
          >
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Wifi className="w-3.5 h-3.5" />}
            Test
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={handleDelete}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-err-bd text-err hover:bg-err-bg disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── Add account form — generic over credential_style ───────────────────────

function AddAccountForm({ group, onCreated }) {
  const [provider, setProvider] = useState(group.providers[0].id);
  const [label, setLabel] = useState('');
  // token style
  const [token, setToken] = useState('');
  // fields style
  const [fields, setFields] = useState({});
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);

  const providerMeta = group.providers.find((p) => p.id === provider) ?? group.providers[0];

  function reset() { setTestResult(null); setError(null); }
  function setField(name, value) { setFields((f) => ({ ...f, [name]: value })); reset(); }

  // Build the credential payload for this group's style.
  const credentials = group.credential_style === 'fields'
    ? Object.fromEntries((providerMeta.fields ?? []).map((f) => [f.name, (fields[f.name] ?? '').trim()]))
    : null;

  const credsFilled = group.credential_style === 'token'
    ? token.trim().length >= 8
    : (providerMeta.fields ?? [])
        .filter((f) => f.required !== false)
        .every((f) => (fields[f.name] ?? '').trim().length > 0);

  const canTest = credsFilled && !testing && !saving;
  const canSave = testResult?.ok && label.trim().length >= 2 && !testing && !saving;

  function testBody() {
    return group.credential_style === 'token'
      ? { provider, token: token.trim() }
      : { provider, credentials };
  }
  function saveBody() {
    return group.credential_style === 'token'
      ? { provider, label: label.trim(), token: token.trim() }
      : { provider, label: label.trim(), credentials };
  }

  async function handleTest() {
    setTesting(true); reset();
    try {
      const r = await api(`${group.base}/test`, { method: 'POST', body: testBody() });
      setTestResult({ ok: true, detail: r.email ?? r.detail ?? 'connection ok' });
    } catch (err) {
      setTestResult({ ok: false, detail: err.body?.detail ?? err.message });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      await api(group.base, { method: 'POST', body: saveBody() });
      onCreated();
    } catch (err) {
      setError(err.body?.detail?.join?.(', ') ?? err.body?.detail ?? err.message);
      setSaving(false);
    }
  }

  const inputCls = 'mt-1 w-full rounded-md border border-bd-strong px-2.5 py-1.5 text-sm';

  return (
    <form onSubmit={handleSave} className="bg-white border border-bd rounded-lg p-5 space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-t1">
        <KeyRound className="w-4 h-4 text-t3" /> Add {group.label.toLowerCase()} account
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs font-medium text-t2">Provider</span>
          <select
            value={provider}
            onChange={(e) => { setProvider(e.target.value); setFields({}); setToken(''); reset(); }}
            className={`${inputCls} bg-white`}
          >
            {group.providers.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-t2">Label</span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={`${providerMeta.label} main`}
            className={inputCls}
          />
        </label>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {group.credential_style === 'token' ? (
          <label className="block sm:col-span-2">
            <span className="text-xs font-medium text-t2">API token</span>
            <input
              type="password"
              value={token}
              onChange={(e) => { setToken(e.target.value); reset(); }}
              placeholder="paste token — stored encrypted"
              autoComplete="off"
              className={`${inputCls} font-mono`}
            />
          </label>
        ) : (
          (providerMeta.fields ?? []).map((f) => (
            <label key={f.name} className={f.multiline ? 'block sm:col-span-2' : 'block'}>
              <span className="text-xs font-medium text-t2">
                {f.label}{f.required === false ? ' (optional)' : ''}
              </span>
              {f.multiline ? (
                <textarea
                  rows={5}
                  value={fields[f.name] ?? ''}
                  onChange={(e) => setField(f.name, e.target.value)}
                  placeholder={f.placeholder}
                  autoComplete="off"
                  className={`${inputCls} font-mono text-xs`}
                />
              ) : (
                <input
                  type={f.secret ? 'password' : 'text'}
                  value={fields[f.name] ?? ''}
                  onChange={(e) => setField(f.name, e.target.value)}
                  placeholder={f.placeholder}
                  autoComplete="off"
                  className={`${inputCls} font-mono`}
                />
              )}
            </label>
          ))
        )}
      </div>

      {testResult && (
        <div className={`text-sm rounded-md px-3 py-2 border ${
          testResult.ok
            ? 'bg-s-green border-bd text-ok'
            : 'bg-err-bg border-err-bd text-err'
        }`}>
          {testResult.ok ? <>Connection OK — {testResult.detail}</> : <>Test failed: {testResult.detail}</>}
        </div>
      )}
      {error && (
        <div className="text-sm rounded-md px-3 py-2 border bg-err-bg border-err-bd text-err">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!canTest}
          onClick={handleTest}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-bd-strong text-t2 hover:bg-s-2 disabled:opacity-50"
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
          Test connection
        </button>
        <button
          type="submit"
          disabled={!canSave}
          title={canSave ? undefined : 'Run a successful connection test first'}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue text-white hover:bg-blue-deep disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Save account
        </button>
        <span className="text-xs text-t3 ml-1">
          Credentials are encrypted with the panel master key and never shown again.
        </span>
      </div>
    </form>
  );
}
